/**
 * GlobalBackButton — a floating "back" affordance shown on every non-root page.
 *
 * Design:
 * - Uses `history.length > 1` + a session-scoped "have we navigated within the
 *   SPA?" flag to decide whether to render. If the user landed straight on the
 *   page from an external link (no in-app history), we DON'T show the button —
 *   `navigate(-1)` would bounce them off the site.
 * - Hides on top-level "home" surfaces where a Back is meaningless:
 *     /                → marketing landing
 *     /login, /register, /recover-with-codes, /auth/callback, /verify-email
 *     /app             → trader dashboard (home)
 *     /admin           → super-admin dashboard (home)
 *     /agency          → broker dashboard (home)
 * - Uses `navigate(-1)`. If for some reason we couldn't go back (shouldn't
 *   happen given the visibility guard above), we fall back to the closest
 *   "home" surface based on the current pathname.
 * - Fixed top-left position, ~76px below the app header, so it never overlaps
 *   the logo but is still one thumb-away on mobile.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

// Never render on these paths — they are natural "home" surfaces.
const HIDE_ON_EXACT = new Set([
    "/",
    "/login",
    "/register",
    "/recover-with-codes",
    "/auth/callback",
    "/verify-email",
    "/app",
    "/admin",
    "/agency",
]);

/** Sensible fallback for the extremely-rare case navigate(-1) can't work. */
function homeFor(pathname) {
    if (pathname.startsWith("/admin"))  return "/admin";
    if (pathname.startsWith("/agency")) return "/agency";
    if (pathname.startsWith("/app"))    return "/app";
    return "/";
}

export default function GlobalBackButton() {
    const location = useLocation();
    const navigate = useNavigate();

    // Track whether the user has performed at least one SPA navigation in this
    // tab. If not, `history.length` isn't a reliable signal (a fresh tab can
    // have length > 1 from a redirect chain) — we'd rather under-show than
    // over-show and boot the user off the site.
    const [hasNavigatedInSpa, setHasNavigatedInSpa] = useState(false);
    useEffect(() => {
        // Fires on every location change AFTER the first mount, i.e. exactly
        // when the user navigates within the SPA.
        setHasNavigatedInSpa(true);
    }, [location.key]);

    const hidden = HIDE_ON_EXACT.has(location.pathname) || !hasNavigatedInSpa;
    if (hidden) return null;

    const goBack = () => {
        try {
            // window.history.state?.idx > 0 tells us react-router has at least
            // one prior entry in this tab. Otherwise fall back to a home surface.
            if ((window.history.state?.idx ?? 0) > 0) {
                navigate(-1);
            } else {
                navigate(homeFor(location.pathname), { replace: true });
            }
        } catch {
            navigate(homeFor(location.pathname), { replace: true });
        }
    };

    return (
        <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            data-testid="global-back-button"
            className="
                fixed left-3 sm:left-4
                top-[68px] sm:top-[76px]
                z-40
                inline-flex items-center gap-1.5
                px-3 py-1.5
                rounded-full
                border border-border
                bg-background/80 backdrop-blur-md
                text-xs font-semibold text-foreground
                shadow-sm
                hover:bg-primary hover:text-primary-foreground hover:border-primary
                transition
            "
        >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Back</span>
        </button>
    );
}
